import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback } from "react";

import { INTAKE_PHYSICIAN_REVIEW_QUEUE_FEATURE } from "constants/feature-flags";
import { Physician } from "services/types/physician.type";

const NEEDS_REVIEW_LABEL_SUFFIX = " (needs review)";

/**
 * Wraps a physician's option/chip text so a physician created from a patient's intake form reads as
 * unreviewed wherever it is offered (SN-19728). Pickers deliberately keep offering those physicians —
 * hiding them made the front desk create the duplicate this ticket fights, and left Front Office
 * unable to pick a patient's own pending PCP — so the state is carried by the label instead.
 *
 * Flag off => the label is returned untouched, so every picker renders exactly as before. Pass the
 * label the picker already builds (`getOptionLabel`, `getPhysicianAsText`, `formatRecipient().name`)
 * rather than formatting a name here.
 */
export default function useNeedsReviewOptionLabel() {
  const flags = useFlags();
  const enabled = Boolean(flags[INTAKE_PHYSICIAN_REVIEW_QUEUE_FEATURE]);

  return useCallback(
    (
      label: string,
      physician?: Pick<Physician, "intake_review_pending"> | null
    ) =>
      enabled && physician?.intake_review_pending
        ? `${label}${NEEDS_REVIEW_LABEL_SUFFIX}`
        : label,
    [enabled]
  );
}
